import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { useCallback, useState } from 'react';
import { fromUnknownError, PermissionError, type AppError } from '@citizen-walk/shared-types';
import { functions, storage } from '../../lib/firebase';

/**
 * `replacePostPhoto` callable wrapper hook (admin posts moderation 写真差し替え、新規機能)。
 *
 * フロー (useAiAnalyzePost のクライアント callable パターン踏襲):
 *   1. 新写真を staging path `districts/{d}/posts/{p}/replacements/incoming-{uuid}.jpg` に
 *      Storage SDK で uploadBytes (storage.rules `canAccessDistrict` で許可される範囲)。
 *   2. callable `replacePostPhoto` を呼出 (Admin SDK が旧画像アーカイブ + canonical 上書き)。
 *   3. busy / error / result state を返す。
 *
 * callable が canonical `original.jpg` を上書きするため、photoPath / 表示層には波及しない。
 */

export interface ReplacePostPhotoCallResult {
  ok: true;
  archivedOriginalPath: string;
  newPhotoUrl: string | null;
}

export type ReplacePostPhotoState =
  | { status: 'idle'; error: null; result: null }
  | { status: 'uploading'; error: null; result: null }
  | { status: 'replacing'; error: null; result: null }
  | { status: 'success'; error: null; result: ReplacePostPhotoCallResult }
  | { status: 'error'; error: AppError; result: null };

export function useReplacePostPhoto(): {
  state: ReplacePostPhotoState;
  busy: boolean;
  replace: (
    districtId: string,
    postId: string,
    file: File,
    reason?: string,
  ) => Promise<ReplacePostPhotoCallResult>;
  reset: () => void;
} {
  const [state, setState] = useState<ReplacePostPhotoState>({
    status: 'idle',
    error: null,
    result: null,
  });

  const replace = useCallback(
    async (districtId: string, postId: string, file: File, reason?: string) => {
      setState({ status: 'uploading', error: null, result: null });
      try {
        // 1. staging path へ upload (canonical を直接上書きせず、callable に委ねる)。
        const uuid = crypto.randomUUID();
        // docs/28 レビュー M2: 差し替える写真 (ぼかす前) は参加者が読めない置き場に上げる。
        const incomingPath = `districts/${districtId}/admin-staging/${postId}/${uuid}.jpg`;
        await uploadBytes(storageRef(storage, incomingPath), file, {
          contentType: file.type || 'image/jpeg',
        });

        // 2. callable 呼出。
        setState({ status: 'replacing', error: null, result: null });
        const fn = httpsCallable<
          { districtId: string; postId: string; incomingPath: string; reason?: string },
          ReplacePostPhotoCallResult
        >(functions, 'replacePostPhoto');
        const res = await fn({ districtId, postId, incomingPath, reason });
        setState({ status: 'success', error: null, result: res.data });
        return res.data;
      } catch (e) {
        let err = fromUnknownError(e);
        if (err instanceof PermissionError) {
          err = new PermissionError(err.code, '写真差し替えの権限がありません。', {
            cause: e,
            details: err.details,
          });
        }
        setState({ status: 'error', error: err, result: null });
        throw err;
      }
    },
    [],
  );

  const reset = useCallback(() => {
    setState({ status: 'idle', error: null, result: null });
  }, []);

  const busy = state.status === 'uploading' || state.status === 'replacing';
  return { state, busy, replace, reset };
}
